/**
 * Pure helpers for reading a session's merge state from its status and metadata.
 * Mirrors the backend rule: a session is locked while it is archived, or while a merge job
 * it belongs to has not finished.
 */

import { METADATA_KEYS } from '../constants';
import type { SessionMergeRecord } from '../types/sessionMerge';

interface SessionMergeSource {
  status: string;
  metadata?: Record<string, any> | null;
}

const MERGE_STATUSES: ReadonlyArray<string> = ['merging', 'merged', 'failed'];

const stringsOf = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && !!item) : [];

/** The merge job record under metadata.merge, or null when the session was never in a merge. */
export const metadataToMergeRecord = (
  metadata?: Record<string, any> | null,
): SessionMergeRecord | null => {
  const record = metadata?.[METADATA_KEYS.MERGE];
  return record && typeof record === 'object' && MERGE_STATUSES.includes(record.status)
    ? (record as SessionMergeRecord)
    : null;
};

/** The source session IDs a merged session was built from (empty for any other session). */
export const metadataToMergedFrom = (metadata?: Record<string, any> | null): string[] =>
  stringsOf(metadata?.[METADATA_KEYS.MERGED_FROM]);

/** The merged session an archived source went into, or null. */
export const metadataToMergedInto = (metadata?: Record<string, any> | null): string | null => {
  const mergedInto = metadata?.[METADATA_KEYS.MERGED_INTO];
  return typeof mergedInto === 'string' && mergedInto ? mergedInto : null;
};

export const isSessionArchived = (session: SessionMergeSource): boolean =>
  session.status === 'archived';

/** True while nobody may change the session: archived, or part of an unfinished merge. */
export const isSessionMergeLocked = (session: SessionMergeSource): boolean => {
  const record = metadataToMergeRecord(session.metadata);
  return isSessionArchived(session) || (record !== null && record.status !== 'merged');
};

/**
 * Whether the session may be deleted. Recording sessions are busy, and archived sources and
 * sessions in an unfinished or failed merge are locked. A completed merged session holds its
 * own copies of every row, so deleting it takes nothing from its sources.
 */
export const isSessionDeletable = (session: SessionMergeSource): boolean =>
  session.status !== 'recording' && !isSessionMergeLocked(session);
